import React, { useState, useEffect } from "react";
import { ConfigurationItem } from "../../../../modules/configuration-hub/data/services/config.service";
import { AssignmentField } from "../../../../modules/configuration-hub/pages/extraction-configuration/add-new-configuration/AddConfigurationModal";
import "./ViewExtractionConfigurationModal.scss";

interface ViewExtractionConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  configurationId: number | null;
  configurationData?: ConfigurationItem | null;
}

const ViewExtractionConfigurationModal: React.FC<
  ViewExtractionConfigurationModalProps
> = ({ isOpen, onClose, configurationId, configurationData }) => {
  const [configData, setConfigData] = useState<ConfigurationItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set configuration data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (configurationData) {
        setConfigData(configurationData);
        setIsLoading(false);
        setError(null);
      } else if (configurationId) {
        setError("Configuration data not found");
        setIsLoading(false);
      }
    }
  }, [isOpen, configurationData, configurationId]);

  const parseFieldConfiguration = (
    config: ConfigurationItem
  ): AssignmentField[] => {
    try {
      if (config.config_payload) {
        const payload = JSON.parse(config.config_payload);

        // Check for field_names_list (actual structure from the data)
        if (
          payload.field_names_list &&
          Array.isArray(payload.field_names_list)
        ) {
          return payload.field_names_list.map((field: any, index: number) => ({
            id: field.id || `field_${index}`,
            fieldName: field.name || field.fieldName || "",
            validationRule: field.validation_rule || field.validationRule || "",
            qualityRule: field.quality_rule || field.qualityRule || "",
            fieldContext: field.field_context || field.fieldContext || ""
          }));
        }

        // Fallback to assignmentFields structure
        if (
          payload.assignmentFields &&
          Array.isArray(payload.assignmentFields)
        ) {
          return payload.assignmentFields;
        }
      }

      return [];
    } catch (parseError) {
      console.error("Error parsing field configuration:", parseError);
      return [];
    }
  };

  if (!isOpen) return null;

  return (
    <div className="view-extraction-modal-overlay">
      <div className="view-extraction-modal-container">
        {/* Modal Header */}
        <div className="view-extraction-modal-header">
          <h2>Extraction Configuration Details</h2>
          <button className="close-button" onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="view-extraction-modal-body">
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading extraction configuration details...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <p>{error}</p>
            </div>
          ) : configData ? (
            <div className="extraction-configuration-details">
              {/* Basic Information */}
              <div className="detail-section">
                <h3 className="section-title">Basic Information</h3>
                <div className="detail-grid">
                  {configData.title && (
                    <div className="detail-item full-width">
                      <label>Configuration Name</label>
                      <div className="detail-value">{configData.title}</div>
                    </div>
                  )}
                  {configData.document_context && (
                    <div className="detail-item full-width">
                      <label>Document Context</label>
                      <div className="detail-value description">
                        {configData.document_context}
                      </div>
                    </div>
                  )}{" "}
                </div>
              </div>

              {/* Configuration Details */}
              {(configData.document_type ||
                configData.document_context ||
                configData.extraction_model) && (
                <div className="detail-section">
                  <h3 className="section-title">Configuration Details</h3>
                  <div className="detail-grid">
                    {configData.document_type && (
                      <div className="detail-item">
                        <label>Document Type</label>
                        <div className="detail-value">
                          {configData.document_type}
                        </div>
                      </div>
                    )}

                    {configData.extraction_model && (
                      <div className="detail-item">
                        <label>Select Model</label>
                        <div className="detail-value">
                          {configData.extraction_model}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Field Configuration */}
              {(() => {
                const fieldConfigs = parseFieldConfiguration(configData);
                const hasValidFields = fieldConfigs.some(
                  (field) =>
                    field.fieldName &&
                    field.fieldName.trim() !== "" &&
                    field.fieldName !== "-"
                );
                return hasValidFields ? (
                  <div className="detail-section">
                    <h3 className="section-title">Field Configuration</h3>
                    <div className="field-configuration-table">
                      <div className="field-table-header">
                        <div className="field-header-cell">Field name</div>
                        <div className="field-header-cell">Validation rule</div>
                        <div className="field-header-cell">Quality rule</div>
                        <div className="field-header-cell">Field context</div>
                      </div>
                      <div className="field-table-body">
                        {fieldConfigs.map((field, index) => (
                          <div
                            key={field.id || index}
                            className="field-table-row"
                          >
                            <div
                              className="field-cell"
                              data-label="Field name:"
                            >
                              <div className="field-value">
                                {field.fieldName || "-"}
                              </div>
                            </div>
                            <div
                              className="field-cell"
                              data-label="Validation rule:"
                            >
                              <div className="field-value">
                                {field.validationRule || "-"}
                              </div>
                            </div>
                            <div
                              className="field-cell"
                              data-label="Quality rule:"
                            >
                              <div className="field-value">
                                {field.qualityRule || "-"}
                              </div>
                            </div>
                            <div
                              className="field-cell"
                              data-label="Field context:"
                            >
                              <div className="field-value">
                                {field.fieldContext || "-"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          ) : null}
        </div>

        {/* Modal Footer */}
        <div className="view-extraction-modal-footer">
          <button className="close-modal-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewExtractionConfigurationModal;
